/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-26 22:37:07
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, getFormValues, reset, change } from 'redux-form';
import { Modal, FormLayout, Link } from '@shopify/polaris';
import history from 'src/routes/history';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import {
  buildInitialSortOption,
  validateSortOptionBeforeSave,
  buildSortOptionValue,
  buildSortTypesOption,
  getTypeValue
} from 'src/helpers/custom-sorting-helper';
import { DEFAULT_SORT_TYPES, SORT_TYPES_ORDER, DATA_TYPES } from 'src/constants/custom-sorting';
import { hasTag } from 'src/helpers/form-validation-helper';

type Props = {
  active: boolean,
  sortOptions: Object,
  handleChange: () => void,
  updateSortOptionItem: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  sortTypeOptions: Array<Object>
};

class SortOptionModal extends Component<ComposedProps, State> {
  state = {
    sortTypeOptions: []
  };

  componentWillReceiveProps(nextProps) {
    const { loading, getSortTypesLoading, active } = nextProps;
    const {
      loading: loadingPrev,
      getSortTypesLoading: getSortTypesLoadingPrev,
      active: activePrev
    } = this.props;

    // Build initial sort types after loading api
    if (
      (!loading && loadingPrev) ||
      (!getSortTypesLoading && getSortTypesLoadingPrev) ||
      (active && !activePrev)
    ) {
      const { sortTypes, sortOptions, sortOption } = nextProps;

      const sortTypeOptions = buildSortTypesOption(sortTypes, sortOptions, sortOption);

      this.setState({ sortTypeOptions });
    }
  }

  render() {
    const { i18n, sortTypes, active, handleSubmit, pristine, sortOption, formValues } = this.props;
    const { sortTypeOptions } = this.state;

    const title = _.isEmpty(sortOption)
      ? i18n.translate('AdminHTML.CustomSorting.Edit.title_Add')
      : i18n.translate('AdminHTML.CustomSorting.Edit.title_Edit');
    const { type = '' } = formValues || {};
    const typeValue = getTypeValue(type, sortTypes);
    const orderOptions =
      typeValue !== ''
        ? SORT_TYPES_ORDER[typeValue].map(item => ({ ...item, label: i18n.translate(item.label) }))
        : [];
    const dataTypeOptions = DATA_TYPES.map(item => ({
      ...item,
      label: i18n.translate(item.label)
    }));
    const tagPrefix = _.get(formValues, 'tagPrefix', '');

    return (
      <Modal
        open={active}
        onClose={this.handleChange}
        title={title}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.save'),
          onAction: handleSubmit(this.handleSave.bind(this)),
          disabled: pristine || typeValue === 'empty-metafield'
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleChange
          }
        ]}>
        <Modal.Section>
          <div className="BC-Modal__CustomSorting">
            <form onSubmit={this.handleDefaultSubmit}>
              <FormLayout>
                <Field
                  name="type"
                  component={RenderSelect}
                  label={i18n.translate('AdminHTML.CustomSorting.CustomSorting.type_Label')}
                  placeholder={i18n.translate(
                    'AdminHTML.CustomSorting.CustomSorting.type_Placeholder'
                  )}
                  options={sortTypeOptions}
                  onChange={(e, value) => this.changeOptionType()}
                />
                {!_.isEmpty(typeValue) && typeValue !== 'empty-metafield' && (
                  <Field
                    name="label"
                    component={RenderTextField}
                    label={i18n.translate('AdminHTML.CustomSorting.CustomSorting.label')}
                    placeholder={
                      DEFAULT_SORT_TYPES.includes(typeValue)
                        ? i18n.translate(
                            'AdminHTML.CustomSorting.CustomSorting.label_Placeholder_Default'
                          )
                        : typeValue === 'tag'
                        ? i18n.translate(
                            'AdminHTML.CustomSorting.CustomSorting.label_Placeholder_Tag'
                          )
                        : i18n.translate(
                            'AdminHTML.CustomSorting.CustomSorting.label_Placeholder_Other'
                          )
                    }
                    validate={[hasTag]}
                  />
                )}
                {!_.isEmpty(orderOptions) && (
                  <Field
                    name="order"
                    component={RenderSelect}
                    label={i18n.translate('AdminHTML.CustomSorting.CustomSorting.order')}
                    placeholder={i18n.translate(
                      'AdminHTML.CustomSorting.CustomSorting.order_Placeholder'
                    )}
                    options={orderOptions}
                  />
                )}
                {typeValue === 'tag' && (
                  <Field
                    name="tagPrefix"
                    component={RenderTextField}
                    label={i18n.translate('AdminHTML.CustomSorting.CustomSorting.tagPrefix')}
                    placeholder={i18n.translate(
                      'AdminHTML.CustomSorting.CustomSorting.tagPrefix_Placeholder'
                    )}
                    tooltip={i18n.translate(
                      'AdminHTML.CustomSorting.CustomSorting.tagPrefix_Tooltip'
                    )}
                    validate={[hasTag]}
                    onChange={value => this.changeTagPrefix(value)}
                  />
                )}
                {(typeValue === 'tag' || typeValue === 'metafield') && (
                  <Field
                    name="dataType"
                    component={RenderSelect}
                    disabled={typeValue === 'tag' && _.isEmpty(tagPrefix)}
                    label={i18n.translate('AdminHTML.CustomSorting.CustomSorting.dataType')}
                    tooltip={i18n.translate(
                      'AdminHTML.CustomSorting.CustomSorting.dataType_Tooltip',
                      {
                        nt_break: <br />,
                        t1: i18n.translate(
                          `AdminHTML.CustomSorting.CustomSorting.dataType_Tooltip_${
                            typeValue === 'tag' ? 'tag' : 'metafield'
                          }`
                        )
                      }
                    )}
                    placeholder={i18n.translate(
                      'AdminHTML.CustomSorting.CustomSorting.dataType_Placeholder'
                    )}
                    options={dataTypeOptions}
                  />
                )}
                {typeValue === 'empty-metafield' && (
                  <span>
                    <span style={{ color: '#DE3618', fontWeight: 500 }}>
                      {i18n.translate('AdminHTML.Common.Title.important')}
                    </span>{' '}
                    {i18n.translate('AdminHTML.CustomSorting.CustomSorting.emptyMetafield', {
                      t1: (
                        <Link
                          onClick={() => {
                            history.push('/metafield');
                          }}>
                          {i18n.translate(
                            'AdminHTML.CustomSorting.CustomSorting.emptyMetafield_t1'
                          )}
                        </Link>
                      )
                    })}
                  </span>
                )}
              </FormLayout>
            </form>
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  // Reset data when change sort type
  changeOptionType = () => {
    const { updateSortOptionModalForm } = this.props;
    const blankData = {
      label: '',
      order: '',
      tagPrefix: '',
      dataType: ''
    };
    Object.keys(blankData).forEach(item => updateSortOptionModalForm(item, blankData[item]));
  };

  changeTagPrefix = value => {
    const { updateSortOptionModalForm } = this.props;
    if (_.isEmpty(value)) {
      updateSortOptionModalForm('dataType', '');
    }
  };
  handleChange = () => {
    this.props.resetForm();
    this.props.handleChange();
  };

  handleSave = data => {
    const { i18n, index, updateSortOption, sortOptions } = this.props;

    if (validateSortOptionBeforeSave(data, sortOptions, index, i18n)) {
      const sortOption = buildSortOptionValue(data);
      updateSortOption(sortOption, index);
    }
    this.handleChange();
  };

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }
}

const SortOptionModalForm = reduxForm({
  form: 'SortOptionModalForm', // a unique identifier for this form
  enableReinitialize: true
})(SortOptionModal);

const mapStateToProps = (state, props) => {
  const { sortOption } = props;
  const formValues = getFormValues('SortOptionModalForm')(state);
  return {
    formValues,
    getSortTypesLoading: _.get(state.customSorting, 'getSortTypesLoading', false),
    getSortTypesSuccess: _.get(state.customSorting, 'getSortTypesSuccess', false),
    loading: _.get(state.customSorting, 'loading', false),
    sortTypes: _.get(state.customSorting, 'sortTypes', []),
    initialValues: buildInitialSortOption(sortOption)
  };
};

const mapDispatchToProps = dispatch => ({
  // Reset form
  resetForm: () => dispatch(reset('SortOptionModalForm')),
  updateSortOptionModalForm: (field, data) => {
    dispatch(change(`SortOptionModalForm`, field, data));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(SortOptionModalForm));
